<template>
  <Transition name="route-changed-toast">
    <div v-if="visible" class="route-changed-toast" :style="{ bottom: `${bottom}px` }" role="status" aria-live="polite">
      <span class="material-icons route-changed-toast__icon" aria-hidden="true">alt_route</span>
      <span class="route-changed-toast__text">{{ $t('plan_trail.route_changed_notice.text') }}</span>
      <button type="button" class="route-changed-toast__undo" @click="undo">
        {{ $t('plan_trail.route_changed_notice.undo') }}
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { ROUTE_CHANGED_EVENT, shouldShowRouteChangedNotice } from '../utils/routeChangedNotice';
import { bottomNoticeOffsetPx, elevationPanelTop } from '../utils/bottomNoticePlacement';

const SHOW_FOR_MS = 6000;
/** The routing indicator's height plus a gap — this toast sits above it while both show. */
const ROUTING_INDICATOR_STACK_PX = 44;

const store = useStore();
const visible = ref(false);

// Above the elevation profile, and above the routing indicator while a
// re-route is still in flight — placed when shown, and again on resize.
const routing = computed(() => !!store.getters?.['planTrail/routingInProgress']);
const bottom = ref(32);
function place() {
  bottom.value = bottomNoticeOffsetPx({
    panelTop: elevationPanelTop(),
    viewportHeight: window.innerHeight,
    stackedBy: routing.value ? ROUTING_INDICATOR_STACK_PX : 0,
  });
}
watch(routing, place);
let shown = 0;
let hideTimer: number | null = null;

function hide() {
  visible.value = false;
  if (hideTimer !== null) window.clearTimeout(hideTimer);
  hideTimer = null;
}

function onRouteChanged() {
  if (!shouldShowRouteChangedNotice(!!store.state.planTrail?.editingExistingData, shown)) return;
  shown += 1;
  place();
  visible.value = true;
  if (hideTimer !== null) window.clearTimeout(hideTimer);
  hideTimer = window.setTimeout(hide, SHOW_FOR_MS);
}

async function undo() {
  hide();
  await store.dispatch('planTrail/undo');
}

onMounted(() => {
  window.addEventListener(ROUTE_CHANGED_EVENT, onRouteChanged);
  window.addEventListener('resize', place);
});
onBeforeUnmount(() => {
  window.removeEventListener(ROUTE_CHANGED_EVENT, onRouteChanged);
  window.removeEventListener('resize', place);
  if (hideTimer !== null) window.clearTimeout(hideTimer);
});
</script>

<style scoped>
.route-changed-toast {
  position: fixed;
  left: 50%;
  bottom: 2rem;
  transform: translateX(-50%);
  z-index: 1500;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: min(560px, calc(100vw - 32px));
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border-radius: 999px;
  background: var(--gray, #22242A);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  font-size: 0.9rem;
}

.route-changed-toast__icon { font-size: 1.15rem; color: #fddf00; }

.route-changed-toast__undo {
  border: none;
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  background: #fddf00;
  color: #17181d;
  font-weight: 600;
  font-size: 0.85rem;
}

.route-changed-toast__undo:hover,
.route-changed-toast__undo:focus-visible { background: #fff; }

.route-changed-toast-enter-active,
.route-changed-toast-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }

.route-changed-toast-enter-from,
.route-changed-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
